// The presets' pure half — 05i-1. main.js's calculatePackedModuleLayout (+ its first-fit
// slot search), the "All" preset's group-by-group stacking and a saved dashboard's module
// list, copied exactly. No DOM: the live half (presets.ts) reads the side panel and the
// module sizes and hands them in.

import type { SavedPreset } from "./saved-presets.js";
import type { PlayerId } from "../data/players.js";

export interface ModuleDef {
  title: string;
  cols: number;
  rows: number;
}

export type PackedItem<T extends ModuleDef> = T & { col: number; row: number };

function isPackedAreaClear(placed: PackedItem<ModuleDef>[], col: number, row: number, cols: number, rows: number): boolean {
  return !placed.some(
    (item) => col < item.col + item.cols && col + cols > item.col && row < item.row + item.rows && row + rows > item.row,
  );
}

function findFirstPackedSlot(placed: PackedItem<ModuleDef>[], cols: number, rows: number, gridCols: number): { col: number; row: number } {
  // Row-major first fit: across the canvas, then down — deterministic, and any size in
  // modules.json fits without overlaps.
  for (let row = 0; row < 10000; row++) {
    for (let col = 0; col <= gridCols - cols; col++) {
      if (isPackedAreaClear(placed, col, row, cols, rows)) return { col, row };
    }
  }
  // main.js's "extremely defensive" fallback — the loop above always finds space.
  const bottomRow = placed.reduce((max, item) => Math.max(max, item.row + item.rows), 0);
  return { col: 0, row: bottomRow };
}

/** main.js calculatePackedModuleLayout: place each module at the first free cell, in order.
 *  Anything else a def carries (e.g. "All"'s tile context) passes through. */
export function packModules<T extends ModuleDef>(defs: T[], gridCols: number): PackedItem<T>[] {
  const placed: PackedItem<T>[] = [];
  defs.forEach((def) => {
    // main.js's `Number(x) || 1` — kept, so an odd size in modules.json lands as 1.
    const cols = Math.max(1, Math.min(gridCols, Number(def.cols) || 1));
    const rows = Math.max(1, Number(def.rows) || 1);
    const slot = findFirstPackedSlot(placed, cols, rows, gridCols);
    // Spread first so the packed values win over anything the def carried under those names.
    placed.push({ ...def, title: def.title, cols, rows, col: slot.col, row: slot.row });
  });
  return placed;
}

const bottomOf = (items: { row: number; rows: number }[]): number =>
  items.reduce((max, item) => Math.max(max, item.row + item.rows), 0);

/** The "All" preset: each side-panel group packed on its own, stacked with one empty row
 *  between groups. `bottomRow` sizes the canvas (+4). */
export function allPresetLayout<T extends ModuleDef>(groups: T[][], gridCols: number): { items: PackedItem<T>[]; bottomRow: number } {
  const items: PackedItem<T>[] = [];
  let startRow = 0;
  groups.forEach((defs) => {
    const groupLayout = packModules(defs, gridCols);
    const groupBottom = bottomOf(groupLayout);
    groupLayout.forEach((item) => items.push({ ...item, row: item.row + startRow }));
    startRow += groupBottom + 1;
  });
  return { items, bottomRow: bottomOf(items) };
}

export interface PresetModule {
  title: string;
  col: number;
  row: number;
  cols: number;
  rows: number;
  player: PlayerId | null;
  custom: Record<string, unknown> | null;
}

/** A saved dashboard's modules at modules.json's sizes. `sizeOf` gets main.js's fallback of
 *  1 × 1 (its layouts never carry a size of their own). */
export function savedPresetModules(
  preset: SavedPreset,
  sizeOf: (title: string, fallbackCols: number, fallbackRows: number) => { cols: number; rows: number },
): { modules: PresetModule[]; bottomRow: number } {
  const modules = preset.layout.map((item) => {
    const size = sizeOf(item.title, 1, 1);
    return {
      title: item.title,
      col: item.col,
      row: item.row,
      cols: size.cols,
      rows: size.rows,
      player: item.player ?? null,
      custom: item.custom ?? null,
    };
  });
  return { modules, bottomRow: bottomOf(modules) };
}
